/**
 * ── OS CARREGADORES: QUE LÍNGUAS EXISTEM, E COMO ELAS DESCEM ───────────────
 *
 * Eles moram fora do `motor.ts` por uma razão de BYTES, não de arrumação: o
 * motor do servidor importa `pt` com as 533 chaves, e qualquer módulo da tela
 * do aluno que o importasse arrastaria o dicionário inteiro para o pedaço de
 * entrada. A tela precisa dos carregadores (é ela que pede a língua do aluno)
 * e **não** precisa do `pt` completo — logo os dois não podem viver no mesmo
 * ficheiro.
 *
 * ## A `A4` mora aqui: idioma sem dicionário não entra na lista
 *
 * As chaves de `CARREGADORES` **são** a resposta, e é por isso que ele não é um
 * `Record<IdiomaDoPoliglota, …>`: uma língua do catálogo sem dicionário
 * simplesmente não aparece aqui, e `idiomasComDicionario()` deixa de a
 * oferecer sem que ninguém mantenha uma segunda lista. Habilitar uma língua
 * sem dicionário produziria o aluno que a `R7` descreve — a navegar em
 * português um curso em finlandês.
 *
 * ## O valor é uma FUNÇÃO, e é isso que mantém os pedaços fora da entrada
 *
 * A primeira escrita importava os seis dicionários no topo do motor, e a cerca
 * `o-editor-nao-entra-na-porta` — que pesa o pedaço de entrada — acusou
 * **429.128 B contra um teto de 339.600**. Eram 87 kB de dicionários que
 * ninguém naquela página ia ler.
 *
 * ## `pt-BR` devolve o CONTEÚDO, e não a casca
 *
 * Porque a casca já está carregada nos dois universos: no servidor ela faz
 * parte do `pt`, e na tela ela é a base do motor. O que falta, em português, é
 * o conteúdo — e é ele que desce.
 */

import type { Folha } from './nucleo.js';

export const CARREGADORES: Readonly<Record<string, () => Promise<Folha>>> = {
  'pt-BR': () => import('./conteudo.js').then((m) => m.CONTEUDO),
  'en-US': () => import('./en-US.js').then((m) => m.en),
  'es-ES': () => import('./es-ES.js').then((m) => m.es),
  'fr-FR': () => import('./fr-FR.js').then((m) => m.fr),
  'de-DE': () => import('./de-DE.js').then((m) => m.de),
  'it-IT': () => import('./it-IT.js').then((m) => m.it),
};

/** Os códigos que têm dicionário — a lista que a tela do tutor oferece (`A4`). */
export function idiomasComDicionario(): string[] {
  return Object.keys(CARREGADORES);
}

/** Há dicionário para esta língua? */
export function temDicionario(idioma: string | null | undefined): boolean {
  return Boolean(idioma && idioma in CARREGADORES);
}

/**
 * Busca o dicionário de uma língua; `{}` quando ela não tem (cai tudo no pt).
 *
 * Nunca lança: um pedaço que não desceu (rede caída a meio) não pode derrubar
 * a área — a tela fica em português, que é o que ela fazia antes desta obra.
 */
export async function carregarDicionario(idioma: string | null | undefined): Promise<Folha> {
  if (!idioma) return {};
  const carregar = CARREGADORES[idioma];
  if (!carregar) return {};
  try {
    return await carregar();
  } catch {
    return {};
  }
}
